import { afterNextRender, Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Addon, AddonType, IMember, IStmxHost } from "ngx-exdr-stmx-common";
import { Observable, catchError, map, of, switchMap, tap, throwError } from "rxjs";
import { CustomDomainService } from "./custom-domain.service";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpHeaderResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { PageTitleEnum } from "../enums/page-title.enum";
import { TranslateService } from "@ngx-translate/core";
import { ExdrLanguageService } from "../../languages/services/exdr-language.service";
import { TabsManagementService } from "../../layout/services/tabs-management.service";
import { isPlatformBrowser } from "@angular/common";
import { AppAppearanceService } from "./app-appearance.service";

const IDENEDI_USER_ID = "idenedi.user.id";
const EXCEED_IDENEDI_GROUP_ID_KEY = "exceed.user.idenedi.group.id";
export const AUTH_TYPE_HEADER = "x-auth-type";
const TOKEN_EXPIRES_AT_KEY = "idenedi.auth.token-expires-at";
const REFRESH_TOKEN_STORAGE_KEY = "idenedi.auth.refresh-token";
const NO_TOKEN_ERROR = "no.token.error";

@Injectable({
  providedIn: 'root'
})
export class StmxHostService implements IStmxHost {
  private _expiresAt: number;
  
  constructor(private customDomainService: CustomDomainService, private http: HttpClient, 
    private exdrLanguageService:ExdrLanguageService, private tabsManagementService: TabsManagementService,    
    @Inject(PLATFORM_ID) private platformId: string,    
    private appAppearanceService: AppAppearanceService
  ) {    
  }

  /**
   * Get addons for current group
   * Method will read group id from html source or from server on basis of dommain address
   * @returns Observable of Addon[]
   */
  getAddons(): Observable<Addon[]> {
    // return this.customDomainService.getAddons();

    return this.customDomainService.getAddons().pipe(
      map(addons => {
        addons.forEach(addon => {
          //check if addon.PublicProperties.bannerData is string then parse it to object
          if(typeof addon.PublicProperties.bannerData === "string"){
            addon.PublicProperties.bannerData = JSON.parse(addon.PublicProperties.bannerData)
          }
        });
        // Deep copy the addons array using JSON.parse(JSON.stringify())
        return JSON.parse(JSON.stringify(addons)) as Addon[];
      })
    );
  }

  /**
   * Get addon on basis of type for current group
   * * Method will read group id from html source or from server on basis of dommain address
   * @param type AddonType
   */
  getAddon(type: AddonType): Observable<Addon | null> {
    return this.customDomainService.getAddons().pipe(
      switchMap(addons => addons.find(a => a.Type.toLowerCase() === type.toLowerCase()) ? of(addons.find(a => a.Type.toLowerCase() === type.toLowerCase()) as Addon) : of(null))
    )
  }

  isLoggedIn(): boolean {
    return false;
  }

  // TODO 1 July
  // isLoggedIn(): Observable<boolean> {
  //   return of(true);
  // }

  clearCachedAddons(): Observable<null> {
    return of(null);
  }

  getCurrentUserId(): (string | undefined) {
    if(isPlatformBrowser(this.platformId)){
      return localStorage.getItem(IDENEDI_USER_ID);
    }
    return undefined
  }



  getUserDetail(idenedi: string): Observable<IMember> {
    if (idenedi != null) {
      if(isPlatformBrowser(this.platformId)){
        localStorage.setItem(EXCEED_IDENEDI_GROUP_ID_KEY, idenedi);
      }      
    }
    const options = {
      headers: new HttpHeaderResponse({
        // @ts-ignore
        [AUTH_TYPE_HEADER]: AddonType.Idenedi,
      }),
    };
    // @ts-ignore
    return this.http.get<any>(
      `${environment.apiHost}/api/user/${idenedi}`,
      // @ts-ignore
      options
    );
  }

  get idenediTokenExpiresAt(): number {
    if (!this._expiresAt) {
      this._expiresAt = +localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
    }
    return this._expiresAt;
  }

  set idenediTokenExpiresAt(value: number) {
    this._expiresAt = value;
    if (value) {
      localStorage.setItem(TOKEN_EXPIRES_AT_KEY, value + "");
    } else {
      localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
    }
  }

  isIdenediTokenExpired(): Observable<boolean> {
    if (!this.idenediTokenExpiresAt) {
      return of(false);
    }
    // 1 minute delay to refresh token
    return of(Date.now() > this.idenediTokenExpiresAt - 60 * 1000);
  }


  tryRefreshIdenediToken(): Observable<any> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!refreshToken) {
      throw NO_TOKEN_ERROR;
    }
    return this.refreshToken(refreshToken).pipe(
      map(({ access_token }) => access_token)
    );
  }

  refreshToken(refreshToken: string): Observable<any> {
    const body = new HttpParams({
      fromObject: {
        grant_type: "refresh_token",
        client_id: environment.authClientId,
        client_secret: environment.authClientSecret,
        refresh_token: refreshToken,
      },
    });
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    return this.http
      .post<any>(
        `${environment.apiHost}/oauth/token`,
        body.toString(),
        { headers }
      )
      .pipe(
        tap(({ access_token, refresh_token, expires_in, idenedi }) => {
          // TODO 20June
          // this.idenediToken = access_token;
          this.idenediTokenExpiresAt = Date.now() + expires_in * 1000;
          localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh_token);
          localStorage.setItem(IDENEDI_USER_ID, idenedi);
        }),
        catchError((error) => {
          setTimeout(() => {
            if (this.isIdenediTokenExpired()) {
              // TODO 20June
              // this.logout();
            }
          }, 500)
          return throwError(() => error);
        })
      );
  }

  getPageTitle(key): Observable<string | null> {
    return of(PageTitleEnum[key]);
  }


  getTranslateDirection(): boolean {
    let currentDirection = this.exdrLanguageService.currentLanguage ? this.exdrLanguageService.currentLanguage.isRtl: false;
    return currentDirection;
  }

  getTranslateLocale(): string {
    let currentDirection = this.exdrLanguageService.currentLanguage ? this.exdrLanguageService.currentLanguage.locale: 'en';
    return currentDirection;
  }

  getGroupId(): string {
    let groupId: string;
      this.customDomainService.getGroupId().subscribe(
      (id: string) => {
        groupId = id
      });
      return groupId;
  }

  getGroupInformation(groupId: string): Observable<any> {    
    return this.customDomainService.getGroupInformation(groupId)
  }

  getHeaderTabsFiltered(filter?): Observable<any> {    
    return this.tabsManagementService.getHeaderTabsFiltered(filter)
  }

  getAppAppearanceData(){
    return this.appAppearanceService.getAppAppearanceData(this.getGroupId());
  }

  getDefaultTheme(){
    return this.appAppearanceService.getDefaultTheme();
  }

  getThemeColors(theme:number){
    return this.appAppearanceService.getThemeColors(theme);
  }

  
}